import type { SavedViewConfig } from "@/components/shared/UniversalCRMTable/types";
import {
  DELIVERY_STAGES,
  SALES_PIPELINE_STAGES,
} from "@/lib/constants/deal-stages";
import { DEAL_FIELDS, type DealFieldId } from "./deal-field-ids";
import { collectFieldsForSavedView } from "./_helpers";

/**
 * Derive a saved view's `visibleColumns` from each field declaration's
 * `savedViewMembership`. Order of returned ids follows field declaration
 * order in `DEAL_FIELDS`; the saved-view consumer in `UniversalEntityPageContent`
 * uses `.includes(fieldId)` so this ordering is not semantically significant.
 */
const visibleColumnsFor = (viewId: string): DealFieldId[] =>
  collectFieldsForSavedView<DealFieldId>(viewId, DEAL_FIELDS);

export const dealSavedViews: SavedViewConfig<DealFieldId>[] = [
  // Most Critical - Default & Main Pipelines
  {
    id: "all-deals",
    name: "All Deals",
    filters: {},
    isDefault: true,
    visibleColumns: visibleColumnsFor("all-deals"),
  },
  /*
   * "My Deals" sits SECOND — immediately after the default — because it is the
   * view a rep reaches for most. It used to be last of fifteen, past the end of
   * a tab strip that scrolls with a hidden scrollbar, so it was effectively
   * undiscoverable. "All Deals" stays the default view.
   */
  {
    id: "my-deals",
    name: "My Deals",
    filters: {
      filter_assigned_users: ["current_user"],
    },
    visibleColumns: visibleColumnsFor("my-deals"),
  },
  {
    id: "sales-pipeline",
    name: "Sales Pipeline",
    filters: {
      filter_stage: [...SALES_PIPELINE_STAGES],
    },
    visibleColumns: visibleColumnsFor("sales-pipeline"),
  },
  {
    id: "not-followed-up-this-week",
    name: "Not Followed Up This Week",
    filters: {
      filter_not_followed_up_this_week: true,
    },
    visibleColumns: visibleColumnsFor("not-followed-up-this-week"),
  },
  {
    id: "scheduled-followups",
    name: "Scheduled Follow-Ups",
    filters: {
      filter_scheduled_followup: true,
    },
    visibleColumns: visibleColumnsFor("scheduled-followups"),
  },
  {
    id: "in-progress",
    name: "In Progress",
    filters: {
      filter_stage: ["in_progress"],
    },
    visibleColumns: visibleColumnsFor("in-progress"),
  },
  {
    id: "closed-deals",
    name: "Closed Deals",
    filters: {
      filter_stage: [...DELIVERY_STAGES],
    },
    visibleColumns: visibleColumnsFor("closed-deals"),
  },

  // CEO Alerts - Requires Immediate Action
  {
    id: "overdue-deals",
    name: "Overdue Deals",
    filters: {
      // Lazy on purpose. `dealSavedViews` is a module-scope const, so evaluating
      // `new Date()` here would pin "overdue" to whenever the module was first
      // loaded — the cutoff then silently drifts for the life of the server
      // process. A getter recomputes it each time the view is actually applied.
      // It is also what `cacheComponents` requires: an unstable value read
      // during a prerender is a hard build error, because the prerendered HTML
      // would bake in the build date.
      get filter_close_date_max(): string {
        return new Date().toISOString().split("T")[0];
      },
      filter_stage: [...SALES_PIPELINE_STAGES],
    },
    visibleColumns: visibleColumnsFor("overdue-deals"),
  },
  {
    id: "unassigned-deals",
    name: "Unassigned Deals",
    filters: {
      filter_assigned_users: ["unassigned"],
    },
    visibleColumns: visibleColumnsFor("unassigned-deals"),
  },
  {
    id: "at-risk",
    name: "At Risk (Low Probability)",
    filters: {
      filter_probability_max: 30,
    },
    visibleColumns: visibleColumnsFor("at-risk"),
  },

  // Critical Delivery Stages - Revenue Recognition
  {
    id: "won-deals",
    name: "Won (Not Started)",
    filters: {
      filter_stage: ["deal_won"],
    },
    visibleColumns: visibleColumnsFor("won-deals"),
  },
  {
    id: "deals-ready-to-invoice",
    name: "Ready to Invoice",
    filters: {
      filter_stage: ["completed"],
    },
    visibleColumns: visibleColumnsFor("deals-ready-to-invoice"),
  },
  {
    id: "deals-invoiced",
    name: "Invoiced (Not Paid)",
    filters: {
      filter_stage: ["invoiced"],
    },
    visibleColumns: visibleColumnsFor("deals-invoiced"),
  },

  // Business Model Views - Strategic
  {
    id: "recurring-revenue",
    name: "Recurring Revenue",
    filters: {
      filter_is_recurring: true,
    },
    visibleColumns: visibleColumnsFor("recurring-revenue"),
  },
  {
    id: "committed-revenue",
    name: "Committed Revenue",
    filters: {
      filter_is_committed: true,
    },
    visibleColumns: visibleColumnsFor("committed-revenue"),
  },

];
